import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Plus, Edit, Trash2, Power, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminNotices({ user, setUser }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchNotices();
  }, [user, navigate]);

  const fetchNotices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/notices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setNotices(data.notices || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar avisos');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (noticeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/notices/${noticeId}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Aviso actualizado');
        fetchNotices();
      }
    } catch (error) {
      toast.error('Error al actualizar aviso');
    }
  };

  const handleDelete = async (noticeId) => {
    if (!confirm('¿Estás seguro de eliminar este aviso?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/notices/${noticeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Aviso eliminado');
        fetchNotices();
      }
    } catch (error) {
      toast.error('Error al eliminar aviso');
    }
  };

  const handleSave = async (noticeData) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingNotice 
        ? `${BACKEND_URL}/api/admin/notices/${editingNotice.id}`
        : `${BACKEND_URL}/api/admin/notices`;
      
      const response = await fetch(url, {
        method: editingNotice ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(noticeData)
      });
      
      if (response.ok) {
        toast.success(editingNotice ? 'Aviso actualizado' : 'Aviso creado');
        setShowForm(false);
        setEditingNotice(null);
        fetchNotices();
      }
    } catch (error) {
      toast.error('Error al guardar aviso');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const getTypeColor = (type) => {
    const colors = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      vacation: 'bg-purple-100 text-purple-800',
      shipping_delay: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100';
  };

  const getTypeLabel = (type) => {
    const labels = {
      info: 'Información',
      warning: 'Advertencia',
      vacation: 'Vacaciones',
      shipping_delay: 'Retraso en Envíos'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6">
            <Link to="/admin" className="text-gray-600 hover:text-black">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black">Solicitudes</Link>
            <Link to="/admin/notices" className="font-medium text-black border-b-2 border-black pb-2">Avisos</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Gestión de Avisos</h2>
          <Button onClick={() => { setEditingNotice(null); setShowForm(true); }} className="bg-black text-white">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Aviso
          </Button>
        </div>

        {showForm ? (
          <NoticeForm 
            notice={editingNotice} 
            onSave={handleSave} 
            onCancel={() => { setShowForm(false); setEditingNotice(null); }}
          />
        ) : (
          <div className="bg-white border-2 border-black">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left p-4">Título</th>
                    <th className="text-left p-4">Tipo</th>
                    <th className="text-left p-4">Fechas</th>
                    <th className="text-left p-4">Estado</th>
                    <th className="text-right p-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {notices.length > 0 ? (
                    notices.map((notice) => (
                      <tr key={notice.id} className="border-b">
                        <td className="p-4">
                          <p className="font-medium">{notice.title}</p>
                          <p className="text-sm text-gray-600">{notice.message.substring(0, 80)}...</p>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${getTypeColor(notice.type)}`}>
                            {getTypeLabel(notice.type)}
                          </span>
                        </td>
                        <td className="p-4 text-sm">
                          {notice.start_date && notice.end_date ? (
                            <span>{notice.start_date} - {notice.end_date}</span>
                          ) : (
                            <span className="text-gray-400">Sin fechas</span>
                          )}
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => handleToggle(notice.id)}
                            className={`flex items-center gap-2 px-3 py-1 rounded ${
                              notice.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <Power className="w-4 h-4" />
                            {notice.is_active ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="p-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => { setEditingNotice(notice); setShowForm(true); }}
                            className="mr-2"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(notice.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-gray-500">
                        <AlertCircle className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                        <p>No hay avisos creados</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NoticeForm({ notice, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: notice?.title || '',
    message: notice?.message || '',
    type: notice?.type || 'info',
    is_active: notice?.is_active !== false,
    start_date: notice?.start_date || '',
    end_date: notice?.end_date || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...notice, ...formData });
  };

  return (
    <div className="bg-white p-6 border-2 border-black mb-6">
      <h3 className="text-xl font-bold mb-4">{notice ? 'Editar Aviso' : 'Nuevo Aviso'}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label>Título *</Label>
          <Input 
            value={formData.title} 
            onChange={(e) => setFormData({...formData, title: e.target.value})} 
            placeholder="Ej: Cerrado por vacaciones"
            required 
          />
        </div>
        
        <div>
          <Label>Mensaje *</Label>
          <textarea 
            className="w-full border-2 border-gray-300 p-3 rounded focus:border-black" 
            rows="4"
            value={formData.message} 
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            placeholder="Ej: Estaremos cerrados del 1 al 15 de agosto. Los pedidos se procesarán a partir del 16 de agosto."
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Tipo de Aviso *</Label>
            <select 
              className="w-full border-2 border-gray-300 p-2 rounded focus:border-black" 
              value={formData.type} 
              onChange={(e) => setFormData({...formData, type: e.target.value})}
            >
              <option value="info">Información</option>
              <option value="warning">Advertencia</option>
              <option value="vacation">Vacaciones</option>
              <option value="shipping_delay">Retraso en Envíos</option>
            </select>
          </div>
          
          <div>
            <Label>Estado Inicial</Label>
            <select 
              className="w-full border-2 border-gray-300 p-2 rounded focus:border-black" 
              value={formData.is_active ? 'active' : 'inactive'} 
              onChange={(e) => setFormData({...formData, is_active: e.target.value === 'active'})}
            >
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Fecha Inicio (opcional)</Label>
            <Input 
              type="date"
              value={formData.start_date} 
              onChange={(e) => setFormData({...formData, start_date: e.target.value})} 
            />
          </div>
          <div>
            <Label>Fecha Fin (opcional)</Label>
            <Input 
              type="date"
              value={formData.end_date} 
              onChange={(e) => setFormData({...formData, end_date: e.target.value})} 
            />
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded border">
          <p className="text-sm text-gray-600 mb-2"><strong>Vista previa del aviso:</strong></p>
          <div className={`p-3 rounded border-l-4 ${
            formData.type === 'warning' ? 'bg-yellow-50 border-yellow-500 text-yellow-800' :
            formData.type === 'vacation' ? 'bg-purple-50 border-purple-500 text-purple-800' :
            formData.type === 'shipping_delay' ? 'bg-orange-50 border-orange-500 text-orange-800' :
            'bg-blue-50 border-blue-500 text-blue-800'
          }`}>
            <p className="font-bold">{formData.title || 'Título del aviso'}</p>
            <p className="text-sm mt-1">{formData.message || 'Mensaje del aviso'}</p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button type="submit" className="bg-black text-white">Guardar</Button>
          <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        </div>
      </form>
    </div>
  );
}

function handleLogout() {
  localStorage.removeItem('token');
  setUser(null);
  navigate('/');
}